import React, { useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgDriverAndId from '@icons/person.svg?react';
import RingilTextArea from '@components/framework/input/RingilTextArea';
import useForm from '@hooks/useForm/useForm.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import * as yup from 'yup';
import { SecuredConfig } from '@components/security/securedDecoration';
import { findErrorMessage } from '@utils/validationUtils.ts';

interface ComponentProps {
  driverName: string;
  driverId: string;
  onChange: (driverId: string, driverName: string) => void;
  readOnly?: boolean;
  driverNameRequired?: boolean;
  driverIdRequired?: boolean;
  maxLength?: number;
  dataTestId?: string;
  secured?: SecuredConfig;
}

interface Form {
  driverName: string;
  driverId: string;
}

const DriverAndIdTag: React.FC<ComponentProps> = ({
  driverName,
  driverId,
  onChange,
  readOnly,
  driverNameRequired = false,
  driverIdRequired = false,
  maxLength,
  dataTestId,
  secured,
}) => {
  const { tr } = useTranslationLgs();
  const requiredMessage = tr('validation.required', 'Value is required');
  const isBlankValue = (v: string) => (v ?? '').trim() === '';
  const content = [driverName, driverId].filter(t => !isBlankValue(t)).join(', ');
  // A partially filled pair is not filled: hide the value so the tag falls back to ButtonTag's
  // required/empty styling instead of looking complete.
  const requirementUnmet = (driverNameRequired && isBlankValue(driverName)) || (driverIdRequired && isBlankValue(driverId));

  const form = useForm<Form>(
    yup.object().shape({
      driverName: driverNameRequired ? yup.string().nullable().required(requiredMessage).matches(/\S/, requiredMessage) : yup.string().nullable(),
      driverId: driverIdRequired ? yup.string().nullable().required(requiredMessage).matches(/\S/, requiredMessage) : yup.string().nullable(),
    }),
    {
      driverName,
      driverId,
    },
    (values, onSuccess) => save(values, onSuccess),
    false,
    false,
    null,
    'none',
  );

  const pane = () => {
    return (
      <>
        <VerticalFieldAndValue
          label={tr('DriverAndIdTag.driverName', 'Driver name')}
          labelMode={'normal'}
          required={driverNameRequired}
          errorMessage={findErrorMessage(form.validationErrors, 'driverName', tr)}
        >
          <RingilTextArea
            rows={1}
            value={form.values.driverName}
            onChange={v => form.setFieldValue(form.names.driverName, v)}
            maxLength={maxLength}
            className={'resize-none'}
          />
        </VerticalFieldAndValue>
        <VerticalFieldAndValue
          label={tr('DriverAndIdTag.driverId', 'Driver ID')}
          labelMode={'normal'}
          required={driverIdRequired}
          errorMessage={findErrorMessage(form.validationErrors, 'driverId', tr)}
        >
          <RingilTextArea
            rows={1}
            value={form.values.driverId}
            onChange={v => form.setFieldValue(form.names.driverId, v)}
            maxLength={maxLength}
            className={'resize-none'}
          />
        </VerticalFieldAndValue>
      </>
    );
  };

  // The tag stays mounted while ReservationDetail reloads, so re-seed the editor when the prop
  // changes; otherwise reopening the pane would write a mount-time value back over a newer one.
  useEffect(() => {
    cancel();
  }, [driverName, driverId]);

  const cancel = () => {
    form.clear();
    form.overwriteValues({ driverName, driverId });
  };

  const save = (values: Form, onSuccess?: () => void) => {
    if (onSuccess) onSuccess();
    onChange(values.driverId, values.driverName);
  };

  return (
    <ButtonTag
      label={tr(`DriverAndIdTag.title`, `Driver and ID`)}
      value={content !== '' && !requirementUnmet ? content : undefined}
      icon={<SvgDriverAndId />}
      actionPane={pane()}
      onSave={onSuccess => form.validateAndSendWithSuccess(onSuccess)}
      actionIcon={<SvgTagEdit />}
      autoClose
      readonly={readOnly}
      required={driverNameRequired || driverIdRequired}
      onClose={() => cancel()}
      dataTestId={dataTestId}
      secured={secured}
    />
  );
};

export default DriverAndIdTag;
